import { FC, useCallback, useReducer } from "react";
import styled from "@emotion/styled";
import { MDSButton, MDSButtonVariant, MDSTextVariant, MDSTextField, MDSTextWeight } from "@/mds";
import { observer } from "mobx-react-lite";
import {
  StyledSubExplanation,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { useAppStore } from "@/store";
import { reducer } from "./custom-vocabulary-settings.reducer";
import { SaveStatus } from "./custom-vocabulary-settings.types";

const MAX_KEYTERMS = 100;
const MAX_KEYTERM_LENGTH = 50;

export const CustomVocabularySettings: FC = observer(() => {
  const { store } = useAppStore();

  // Get settings from the store
  const storeCustomVocabulary = store.spaceAccountSettings.customVocabulary;
  const initialVocabString = storeCustomVocabulary.join(", ");

  const [state, dispatch] = useReducer(reducer, {
    originalValue: initialVocabString,
    currentValue: initialVocabString,
    saveStatus: SaveStatus.Idle,
    error: null,
  });

  const { originalValue, currentValue, saveStatus, error } = state;

  const isDirty = currentValue !== originalValue;

  const validate = (value: string): string | null => {
    if (!value.trim()) {
      return null;
    }

    const keyterms = value
      .split(",")
      .map(term => term.trim())
      .filter(term => term.length > 0);

    if (keyterms.length > MAX_KEYTERMS) {
      return `Specify fewer than ${MAX_KEYTERMS} words or phrases`;
    }

    const invalidTerm = keyterms.find(term => term.length > MAX_KEYTERM_LENGTH);
    if (invalidTerm) {
      return `"${invalidTerm.slice(0, 20)}..." is too long. Use commas to separate words or phrases, and ensure each one is less than ${MAX_KEYTERM_LENGTH} characters.`;
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validate(currentValue);
    if (validationError) {
      return;
    }

    dispatch({ type: "START_SAVING" });

    const sanitizedKeyterms = currentValue
      .split(",")
      .map(term => term.trim())
      .filter(term => term.length > 0);

    try {
      const updatedVoiceSettings = await store.spaceAccountSettings.updateSettingsSynchronously({
        voice: {
          custom_vocabulary: sanitizedKeyterms,
        },
      });

      const persistedVocabulary = updatedVoiceSettings?.custom_vocabulary ?? [];
      const persistedVocabString = persistedVocabulary.join(", ");

      dispatch({ type: "SAVE_SUCCESS", value: persistedVocabString });
    } catch (err) {
      logger.error({
        message: "Failed to save custom vocabulary settings",
        info: { error: objectModule.safeErrorAsJson(err as Error) },
      });
      dispatch({ type: "SAVE_ERROR", error: "Failed to save settings. Please try again." });
    }
  };

  const handleCancel = () => {
    dispatch({ type: "CANCEL" });
  };

  const handleChange = useCallback((value: string) => {
    const validationError = validate(value);
    dispatch({ type: "SET_VALUE", value, error: validationError });
  }, []);

  return (
    <FullWidthContainer>
      <HeadingRow>
        <HeadingRowHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
          Custom vocabulary
        </HeadingRowHeading>
      </HeadingRow>
      <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
        Add unique names, acronyms, or industry terms to help Mem transcribe them correctly. Comma
        separate words or phrases.
      </StyledSubExplanation>

      <StyledTextArea
        isTextArea
        placeholder={`ACME Corp, Stefanie, Project Porcupine, Q3 OKRs`}
        value={currentValue}
        onChange={handleChange}
        errorMessage={error ?? undefined}
        textAreaProps={{ rows: 3 }}
        isDisabled={saveStatus === SaveStatus.Saving}
      />

      <ButtonContainer>
        <MDSButton
          variant={MDSButtonVariant.Default}
          onClick={handleCancel}
          isDisabled={!isDirty || saveStatus === SaveStatus.Saving}
        >
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleSave}
          isLoading={saveStatus === SaveStatus.Saving}
          isDisabled={!isDirty || !!error}
        >
          {saveStatus === SaveStatus.Saved ? "Saved" : "Save"}
        </MDSButton>
      </ButtonContainer>
    </FullWidthContainer>
  );
});

const FullWidthContainer = styled.div`
  width: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[7]} ${theme.spacing[2]} 0`};
`;

const HeadingRowHeading = styled(StyledHeading)`
  padding: 0;
`;

const StyledTextArea = styled(MDSTextField)`
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[2]};
`;
